import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    CircularProgress,
    Alert,
    Divider,
    Typography,
    Box,
    Chip,
    FormControlLabel,
    Switch,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Autocomplete from '@mui/material/Autocomplete';
import Grid2 from '@mui/material/Grid2';
import { styled } from '@mui/material/styles';
import { createProject } from '../services/projectService';
import { addProjectMembers } from '../../ProjectDashboard/services/projectDashboardService';
import { fetchAllUsers } from '../../Settings/services/userManagementService';

const FormTextField = styled(TextField)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
        borderRadius: theme.shape.borderRadius,
    },
}));

const initialFormState = {
    name: '',
    fieldId: '',
    streetAddress: '',
    city: '',
    province: '',
    postalCode: '',
    country: 'Canada',
    description: '',
    isActive: true,
    shareInternalDeficienciesExternally: false,
    allowExternalSubmissions: false,
    externalSubmissionDeadline: '',
};

const CreateProjectForm = ({ open, onClose, onProjectCreated, businessId }) => {
    const [formData, setFormData] = useState(initialFormState);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [selectedPMs, setSelectedPMs] = useState([]);
    const [errors, setErrors] = useState({});
    const [isMultiBuilding, setIsMultiBuilding] = useState(false);
    const [buildingNames, setBuildingNames] = useState([]);
    const [newBuildingInput, setNewBuildingInput] = useState('');

    useEffect(() => {
        const loadUsers = async () => {
            try {
                setLoadingUsers(true);
                const allUsers = await fetchAllUsers();
                setUsers(Array.isArray(allUsers) ? allUsers : []);
            } catch (e) {
                console.error('Failed to load users for PM selection', e);
            } finally {
                setLoadingUsers(false);
            }
        };
        if (open) {
            loadUsers();
        }
    }, [open]);

    const handleChange = (e) => {
        const { name, value, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]:
                name === 'isActive' ||
                name === 'shareInternalDeficienciesExternally' ||
                name === 'allowExternalSubmissions'
                    ? checked
                    : value,
        }));

        // Clear error for this field if value becomes valid
        if (errors[name]) {
            const newVal =
                name === 'isActive' ||
                name === 'shareInternalDeficienciesExternally' ||
                name === 'allowExternalSubmissions'
                    ? checked
                    : value;
            if (newVal !== undefined && String(newVal).trim().length > 0) {
                setErrors((prev) => {
                    const { [name]: _removed, ...rest } = prev;
                    return rest;
                });
            }
        }
    };

    const handleAddBuildingName = () => {
        const trimmed = newBuildingInput.trim();
        if (!trimmed) return;
        if (buildingNames.some((b) => b.toLowerCase() === trimmed.toLowerCase())) return;
        setBuildingNames((prev) => [...prev, trimmed]);
        setNewBuildingInput('');
        if (errors.buildingNames) {
            setErrors((prev) => {
                const { buildingNames: _removed, ...rest } = prev;
                return rest;
            });
        }
    };

    const handleRemoveBuildingName = (nameToRemove) => {
        setBuildingNames((prev) => prev.filter((b) => b !== nameToRemove));
    };

    const validate = () => {
        const newErrors = {};
        const requiredText = (val) => !val || String(val).trim() === '';

        if (requiredText(formData.name)) newErrors.name = 'Project name is required';
        if (requiredText(formData.streetAddress))
            newErrors.streetAddress = 'Street address is required';
        if (requiredText(formData.city)) newErrors.city = 'City is required';
        if (requiredText(formData.province)) newErrors.province = 'Province is required';
        if (requiredText(formData.postalCode)) newErrors.postalCode = 'Postal code is required';
        if (!selectedPMs || selectedPMs.length === 0)
            newErrors.internalPMs = 'At least one Internal PM is required';
        if (isMultiBuilding && buildingNames.length === 0)
            newErrors.buildingNames =
                'At least one building name is required for multi-building projects';

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const validation = validate();
            setErrors(validation);
            if (Object.keys(validation).length > 0) {
                setLoading(false);
                return;
            }
            const projectData = {
                ...formData,
                businessId,
                creationDate: new Date().toISOString().split('T')[0],
                floorplanGroupingEnabled: isMultiBuilding,
                buildingNames: isMultiBuilding ? buildingNames : [],
            };

            // Normalize fieldId before sending: send null instead of empty string to avoid DB unique collisions
            if (projectData.fieldId !== undefined && projectData.fieldId !== null) {
                const trimmed = String(projectData.fieldId).trim();
                projectData.fieldId = trimmed.length === 0 ? null : trimmed;
            }

            const result = await createProject(projectData);

            // Add selected Internal PMs to the newly created project
            const projectId = result?.projectId;
            if (!projectId) {
                throw new Error('Project created but missing projectId');
            }

            await addProjectMembers(
                projectId,
                selectedPMs.map((pm) => ({
                    projectId,
                    userId: pm.userId,
                    role: 'INTERNAL_PM',
                }))
            );

            setLoading(false);
            onProjectCreated(result);
            handleClose();
        } catch (err) {
            console.error('Error creating project:', err);
            setError('Failed to create project. Please try again.');
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData(initialFormState);
        setSelectedPMs([]);
        setIsMultiBuilding(false);
        setBuildingNames([]);
        setNewBuildingInput('');
        setError(null);
        setErrors({});
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
            <DialogTitle>Create New Project</DialogTitle>
            <form onSubmit={handleSubmit} noValidate autoComplete="off">
                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Grid2 container spacing={2}>
                        <Grid2 size={12}>
                            <FormTextField
                                name="name"
                                label="Project Name"
                                fullWidth
                                required
                                value={formData.name}
                                onChange={handleChange}
                                error={Boolean(errors.name)}
                                helperText={errors.name || ''}
                            />
                        </Grid2>

                        <Grid2 size={12}>
                            <FormTextField
                                name="fieldId"
                                label="Project ID (Optional)"
                                fullWidth
                                value={formData.fieldId}
                                onChange={handleChange}
                                helperText="A unique identifier for this project within your business"
                            />
                        </Grid2>

                        <Grid2 size={12}>
                            <FormTextField
                                name="streetAddress"
                                label="Street Address"
                                fullWidth
                                required
                                value={formData.streetAddress}
                                onChange={handleChange}
                                error={Boolean(errors.streetAddress)}
                                helperText={errors.streetAddress || ''}
                            />
                        </Grid2>

                        <Grid2 size={{ xs: 12, sm: 4 }}>
                            <FormTextField
                                name="city"
                                label="City"
                                fullWidth
                                required
                                value={formData.city}
                                onChange={handleChange}
                                error={Boolean(errors.city)}
                                helperText={errors.city || ''}
                            />
                        </Grid2>

                        <Grid2 size={{ xs: 12, sm: 4 }}>
                            <FormTextField
                                name="province"
                                label="Province"
                                fullWidth
                                required
                                value={formData.province}
                                onChange={handleChange}
                                error={Boolean(errors.province)}
                                helperText={errors.province || ''}
                            />
                        </Grid2>

                        <Grid2 size={{ xs: 12, sm: 4 }}>
                            <FormTextField
                                name="postalCode"
                                label="Postal Code"
                                fullWidth
                                required
                                value={formData.postalCode}
                                onChange={handleChange}
                                error={Boolean(errors.postalCode)}
                                helperText={errors.postalCode || ''}
                            />
                        </Grid2>

                        <Grid2 size={12}>
                            <FormTextField
                                name="country"
                                label="Country"
                                fullWidth
                                value={formData.country}
                                onChange={handleChange}
                            />
                        </Grid2>

                        <Grid2 size={12}>
                            <FormTextField
                                name="description"
                                label="Description"
                                fullWidth
                                multiline
                                rows={4}
                                value={formData.description}
                                onChange={handleChange}
                            />
                        </Grid2>

                        <Grid2 size={12}>
                            <Autocomplete
                                multiple
                                options={users.filter(
                                    (u) => u.isInternalUser && u.role === 'INTERNAL_PM'
                                )}
                                value={selectedPMs}
                                onChange={(event, newValue) => {
                                    setSelectedPMs(newValue);
                                    if (errors.internalPMs && newValue && newValue.length > 0) {
                                        setErrors((prev) => {
                                            const { internalPMs, ...rest } = prev;
                                            return rest;
                                        });
                                    }
                                }}
                                isOptionEqualToValue={(option, value) =>
                                    option.userId === value.userId
                                }
                                getOptionLabel={(option) =>
                                    `${option.firstName} ${option.lastName} (${option.email})`
                                }
                                loading={loadingUsers}
                                renderInput={(params) => (
                                    <FormTextField
                                        {...params}
                                        label="Internal PM(s)"
                                        placeholder="Select at least one Internal PM"
                                        InputLabelProps={{ required: true }}
                                        error={Boolean(errors.internalPMs)}
                                        helperText={errors.internalPMs || ''}
                                    />
                                )}
                            />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                These users will be added to the project as Internal PMs.
                            </Typography>
                        </Grid2>

                        <Grid2 size={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.isActive}
                                        onChange={handleChange}
                                        name="isActive"
                                        color="primary"
                                    />
                                }
                                label="Active"
                            />
                        </Grid2>

                        <Grid2 size={12}>
                            <Divider sx={{ borderBottomWidth: 2 }} />
                        </Grid2>

                        <Grid2 size={12}>
                            <Typography variant="h5" sx={{ mt: 2, fontWeight: 600 }}>
                                Project Structure
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Define the building structure for this project. This cannot be
                                changed after creation.
                            </Typography>
                        </Grid2>

                        <Grid2 container size={12} spacing={2}>
                            {[
                                {
                                    value: false,
                                    icon: <BusinessIcon sx={{ fontSize: 26 }} />,
                                    title: 'Single Building',
                                    description:
                                        'All drawings are shown in a single list. Best for single-site projects.',
                                },
                                {
                                    value: true,
                                    icon: <LocationCityIcon sx={{ fontSize: 26 }} />,
                                    title: 'Multi-Building',
                                    description:
                                        'Drawings are grouped by building. Use this when the project spans multiple buildings.',
                                },
                            ].map((option) => {
                                const selected = isMultiBuilding === option.value;
                                return (
                                    <Grid2 key={String(option.value)} size={{ xs: 12, sm: 6 }}>
                                        <Box
                                            onClick={() => {
                                                setIsMultiBuilding(option.value);
                                                if (!option.value) {
                                                    setBuildingNames([]);
                                                    setNewBuildingInput('');
                                                }
                                                if (errors.buildingNames) {
                                                    setErrors((prev) => {
                                                        const { buildingNames: _removed, ...rest } =
                                                            prev;
                                                        return rest;
                                                    });
                                                }
                                            }}
                                            sx={{
                                                position: 'relative',
                                                border: '2px solid',
                                                borderColor: selected ? 'primary.main' : '#E4E7EE',
                                                borderRadius: '12px',
                                                p: 2.5,
                                                cursor: 'pointer',
                                                backgroundColor: selected
                                                    ? 'rgba(11, 21, 55, 0.04)'
                                                    : '#fff',
                                                transition:
                                                    'border-color 0.15s, background-color 0.15s',
                                                '&:hover': {
                                                    borderColor: selected
                                                        ? 'primary.main'
                                                        : '#9BA2B0',
                                                    backgroundColor: selected
                                                        ? 'rgba(11, 21, 55, 0.04)'
                                                        : '#F7F8FA',
                                                },
                                                userSelect: 'none',
                                            }}
                                        >
                                            {selected && (
                                                <CheckCircleIcon
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 12,
                                                        right: 12,
                                                        fontSize: 18,
                                                        color: 'primary.main',
                                                    }}
                                                />
                                            )}
                                            <Box
                                                sx={{
                                                    color: selected
                                                        ? 'primary.main'
                                                        : 'text.secondary',
                                                    mb: 1,
                                                    lineHeight: 1,
                                                }}
                                            >
                                                {option.icon}
                                            </Box>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontWeight: 600,
                                                    mb: 0.5,
                                                    color: selected
                                                        ? 'primary.main'
                                                        : 'text.primary',
                                                }}
                                            >
                                                {option.title}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {option.description}
                                            </Typography>
                                        </Box>
                                    </Grid2>
                                );
                            })}

                            {isMultiBuilding && (
                                <Grid2 size={12}>
                                    <Box sx={{ pt: 1 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 600, mb: 0.5 }}
                                        >
                                            Buildings
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mb: 1.5 }}
                                        >
                                            Add each building by name, then press Enter or click
                                            Add. You can remove any before saving.
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 1,
                                                alignItems: 'center',
                                                mb: 1,
                                            }}
                                        >
                                            <TextField
                                                size="small"
                                                placeholder="Building name"
                                                value={newBuildingInput}
                                                onChange={(e) =>
                                                    setNewBuildingInput(e.target.value)
                                                }
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        handleAddBuildingName();
                                                    }
                                                }}
                                                error={Boolean(errors.buildingNames)}
                                                sx={{ flex: 1 }}
                                            />
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={handleAddBuildingName}
                                                disabled={!newBuildingInput.trim()}
                                                sx={{ textTransform: 'none' }}
                                            >
                                                Add
                                            </Button>
                                        </Box>

                                        {errors.buildingNames && (
                                            <Typography
                                                variant="body2"
                                                color="error"
                                                sx={{ mb: 1 }}
                                            >
                                                {errors.buildingNames}
                                            </Typography>
                                        )}

                                        {buildingNames.length > 0 && (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {buildingNames.map((name) => (
                                                    <Chip
                                                        key={name}
                                                        label={name}
                                                        onDelete={() =>
                                                            handleRemoveBuildingName(name)
                                                        }
                                                        variant="outlined"
                                                    />
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                </Grid2>
                            )}
                        </Grid2>

                        <Grid2 size={12}>
                            <Divider sx={{ borderBottomWidth: 2 }} />
                        </Grid2>

                        <Grid2 size={12}>
                            <Typography variant="h5" sx={{ mt: 2, fontWeight: 600 }}>
                                Access Control
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                You can adjust these settings later in the project settings.
                            </Typography>
                        </Grid2>

                        <Grid2 size={12}>
                            <Typography variant="h6" sx={{ fontWeight: 550, mb: 1 }}>
                                Share Internally Created Deficiencies with External Members
                            </Typography>

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.shareInternalDeficienciesExternally}
                                        onChange={handleChange}
                                        name="shareInternalDeficienciesExternally"
                                        color="primary"
                                    />
                                }
                                label={
                                    formData.shareInternalDeficienciesExternally
                                        ? 'Share with External Members'
                                        : 'Not share with External Members'
                                }
                            />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                {formData.shareInternalDeficienciesExternally
                                    ? 'External Project Members can view internally created deficiencies.'
                                    : 'External Project Members can NOT view internally created deficiencies.'}
                            </Typography>
                        </Grid2>

                        {/* divider*/}
                        <Grid2 size={12}>
                            <Divider />
                        </Grid2>

                        <Grid2 size={12}>
                            <Typography variant="h6" sx={{ fontWeight: 550, mb: 1 }}>
                                Allow External Member Submissions
                            </Typography>

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.allowExternalSubmissions}
                                        onChange={handleChange}
                                        name="allowExternalSubmissions"
                                        color="primary"
                                        disabled={loading}
                                    />
                                }
                                label={
                                    formData.allowExternalSubmissions
                                        ? 'External submissions are allowed'
                                        : 'External submissions are not allowed'
                                }
                            />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                {formData.allowExternalSubmissions
                                    ? 'External Project Members can submit new deficiencies.'
                                    : 'External Project Members CANNOT submit new deficiencies.'}
                            </Typography>

                            {/* Cutoff Date Field - only show when external submissions are allowed */}
                            {formData.allowExternalSubmissions && (
                                <Box sx={{ mt: 2 }}>
                                    <TextField
                                        type="date"
                                        label="Submission Deadline Date"
                                        name="externalSubmissionDeadline"
                                        value={formData.externalSubmissionDeadline}
                                        onChange={handleChange}
                                        disabled={loading}
                                        fullWidth
                                        size="small"
                                        slotProps={{
                                            inputLabel: {
                                                shrink: true,
                                            },
                                        }}
                                        helperText="External members can submit deficiencies until this date"
                                    />
                                </Box>
                            )}
                        </Grid2>
                    </Grid2>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button type="submit" variant="contained" color="primary" disabled={loading}>
                        {loading ? <CircularProgress size={24} /> : 'Create Project'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default CreateProjectForm;

CreateProjectForm.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onProjectCreated: PropTypes.func.isRequired,
    businessId: PropTypes.string.isRequired,
};
